import React, { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { API } from '@/config';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Eye, EyeOff, ArrowLeft, Mail, Check } from 'lucide-react';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  // Forgot password form
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  // Reset password form
  const [password, setPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetDone, setResetDone] = useState(false);

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!email) { toast.error('Enter your email'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success('Check your email for the reset link');
    } catch {
      toast.error('Something went wrong. Try again.');
    } finally {
      setSending(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (!password || password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirmPw) { toast.error('Passwords do not match'); return; }
    setResetting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setResetDone(true);
      toast.success('Password reset! Redirecting to login...');
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed. Link may have expired.');
    } finally {
      setResetting(false);
    }
  };

  // Logo component
  const Logo = () => (
    <div className="flex items-center gap-2.5 mb-8">
      <div className="w-8 h-8 rounded-lg bg-[#10B981] flex items-center justify-center">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="10" width="3" height="8" rx="1.5" fill="white" opacity="0.7" />
          <rect x="8" y="6" width="3" height="16" rx="1.5" fill="white" />
          <rect x="13" y="3" width="3" height="18" rx="1.5" fill="white" />
          <rect x="18" y="8" width="3" height="10" rx="1.5" fill="white" opacity="0.7" />
        </svg>
      </div>
      <span className="text-lg font-bold tracking-tight text-foreground">Whisper.AI</span>
    </div>
  );

  // ─── Reset Password (has token) ───
  if (token) {
    if (resetDone) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background px-4">
          <div className="w-full max-w-sm text-center">
            <div className="w-16 h-16 rounded-full bg-[#10B981]/10 flex items-center justify-center mx-auto mb-6">
              <Check className="h-8 w-8 text-[#10B981]" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">Password Reset</h1>
            <p className="text-sm text-muted-foreground">Redirecting to sign in...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="w-full max-w-sm" data-testid="reset-password-form">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
          <Logo />
          <h1 className="text-2xl font-bold text-foreground mb-1">Set new password</h1>
          <p className="text-sm text-muted-foreground mb-6">Enter your new password below.</p>
          <form onSubmit={handleReset} className="space-y-4">
            <div className="space-y-1.5">
              <Label>New Password</Label>
              <div className="relative">
                <Input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min 6 characters"
                  className="pr-10"
                  data-testid="new-password-input"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Confirm Password</Label>
              <Input
                type={showPw ? 'text' : 'password'}
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                placeholder="Confirm new password"
                data-testid="confirm-password-input"
              />
            </div>
            <Button type="submit" className="w-full" disabled={resetting} data-testid="reset-submit-btn">
              {resetting ? 'Resetting...' : 'Reset Password'}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // ─── Forgot Password (no token) ───
  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="w-full max-w-sm text-center" data-testid="reset-email-sent">
          <div className="w-16 h-16 rounded-full bg-[#10B981]/10 flex items-center justify-center mx-auto mb-6">
            <Mail className="h-8 w-8 text-[#10B981]" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Check your email</h1>
          <p className="text-sm text-muted-foreground mb-2">If an account exists for</p>
          <p className="text-sm font-semibold text-foreground mb-6">{email}</p>
          <p className="text-xs text-muted-foreground mb-6">we've sent a password reset link. It expires in 1 hour.</p>
          <div className="space-y-3">
            <Button variant="outline" className="w-full" onClick={() => { setSent(false); setSending(false); }}>
              Try a different email
            </Button>
            <Link to="/login">
              <Button variant="ghost" className="w-full">Back to Sign In</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm" data-testid="forgot-password-form">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </Link>
        <Logo />
        <h1 className="text-2xl font-bold text-foreground mb-1">Forgot password?</h1>
        <p className="text-sm text-muted-foreground mb-6">Enter your email and we'll send you a reset link.</p>
        <form onSubmit={handleForgot} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              autoFocus
              data-testid="forgot-email-input"
            />
          </div>
          <Button type="submit" className="w-full" disabled={sending} data-testid="forgot-submit-btn">
            {sending ? 'Sending...' : 'Send Reset Link'}
          </Button>
        </form>
        <p className="text-sm text-center text-muted-foreground mt-6">
          Remember your password? <Link to="/login" className="text-[#10B981] hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
